
import React, { useEffect } from "react";
import Translate from "../components/Translate";
import { useTranslation } from "react-i18next";
import { updateMetaTags } from "../utils/seo";
import PageBanner from "../components/PageBanner";
import { Link } from "react-router-dom";
import { HiKey, HiLifebuoy, HiWrenchScrewdriver, HiChevronRight } from "react-icons/hi2";

export default function HelpPage() {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        updateMetaTags(
            `${t('help')} | ${t('site-title')}`,
            t('help-desc'),
            t('help-keywords')
        );
    }, [t, i18n.language]);

    const helpLinks = [
        {
            title: t('various_file_formats'),
            desc: t('various_file_formats_desc'),
            path: "/various-file-formats"
        },
        {
            title: t('screen_reader'),
            desc: t('help-screen-reader-desc'),
            path: "/screen-reader-access"
        },
        {
            title: t('speech_recognition_support'),
            desc: t('speech_recognition_desc'),
            path: "/speech-recognition-support"
        },
        {
            title: t('accessibility_statement'),
            desc: t('accessibility_statement_desc'),
            path: "/accessibility-statement"
        }
    ];

    return (
        <section className="w-full min-h-screen bg-primary-50/30 pb-16 text-left">
            <PageBanner
                title={<Translate text={'help'} />}
                subtitle={<Translate text={'help-subtitle'} />}
                badgeText={t('badge-support-center')}
            />

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-[15px]">
                <div className="bg-white rounded-[24px] shadow-sm border border-[#dadce0] overflow-hidden">
                    {/* Main Content Section */}
                    <div className="p-8 border-b border-gray-100">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                             {t('help-title')}
                        </h2>
                        
                        <div className="space-y-10">
                            <section>
                                <p className="text-gray-600 leading-relaxed italic border-l-4 border-primary-500 pl-4 bg-primary-50/30 py-4 rounded-r-lg">
                                    {t('help-intro')}
                                </p>
                            </section>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <section className="p-6 rounded-2xl border border-gray-100 bg-white">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-primary-50 rounded-lg text-primary-600">
                                            <HiKey className="text-xl" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">{t('help-subscription-access')}</h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed text-sm">
                                        {t('help-subscription-desc')}
                                    </p>
                                </section>

                                <section className="p-6 rounded-2xl border border-gray-100 bg-white">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-secondary-50 rounded-lg text-secondary-600">
                                            <HiLifebuoy className="text-xl" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">{t('help-support')}</h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed text-sm">
                                        {t('help-support-desc')}
                                    </p>
                                </section>
                            </div>
                        </div>
                    </div>

                    {/* Accessibility links section */}
                    <div className="p-8 bg-gray-50/50">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                <HiWrenchScrewdriver className="text-xl" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">{t('help-accessibility-info')}</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {helpLinks.map((link, idx) => (
                                <Link 
                                    key={idx} 
                                    to={link.path} 
                                    className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-100 hover:border-primary-400 hover:shadow-md transition duration-300 group"
                                >
                                    <div className="flex-1 min-w-0 pr-4">
                                        <h4 className="text-sm font-bold text-gray-800 group-hover:text-primary-600 transition">
                                            {link.title}
                                        </h4>
                                    </div>
                                    <HiChevronRight className="text-gray-600 group-hover:text-primary-600 transition text-lg" />
                                </Link>
                            ))}
                        </div>

                        {/* Further Assistance */}
                        <div className="mt-12 p-8 rounded-[24px] bg-gradient-to-br from-primary-600 to-primary-800 text-white shadow-lg relative overflow-hidden">
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold mb-3">{t('help-further-help')}</h3>
                                <p className="text-primary-50/80 mb-8 max-w-xl text-base leading-relaxed">
                                    {t('help-further-help-desc')}
                                </p>
                                <Link 
                                    to="/contact" 
                                    className="inline-flex items-center gap-2 bg-white text-primary-700 px-8 py-3 rounded-full font-bold hover:bg-primary-50 transition shadow-lg"
                                >
                                    {t('help-contact-us')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
